<script setup lang="ts">
import { BrainCircuit } from 'lucide-vue-next';
import { computed, watch } from 'vue';
import { useRunProgress } from '@/composables/useRunProgress';

/**
 * RunProgressTracker — composant unifié de suivi de progression IA en temps réel.
 *
 * Utilisable pour tout run AI (screening, extraction, colonnes...).
 * S'abonne au canal `run.{runId}` via useRunProgress et affiche :
 *   - barre de progression animée (shimmer avant le premier update, progression réelle ensuite)
 *   - compteur d'articles traités / total
 *   - titre de l'article en cours
 *
 * @emits completed  — quand le run passe en statut `completed`
 * @emits failed     — quand le run passe en statut `failed`
 * @emits cancelled  — quand le run passe en statut `cancelled`
 */
const props = defineProps<{
    /** ID du Run à suivre (canal `run.{runId}`). */
    runId: string;
    /** Libellé affiché à gauche de la barre. Par défaut : "IA en cours…" */
    featureLabel?: string;
}>();

const emit = defineEmits<{
    completed: [];
    failed: [];
    cancelled: [];
}>();

const { progress, status, currentPaperTitle, papersProcessed, totalPapers } =
    useRunProgress(props.runId);

const countersLabel = computed(() =>
    totalPapers.value > 0
        ? `${papersProcessed.value} / ${totalPapers.value}`
        : '',
);

watch(status, (s) => {
    if (s === 'completed') {
        emit('completed');
    } else if (s === 'failed') {
        emit('failed');
    } else if (s === 'cancelled') {
        emit('cancelled');
    }
});
</script>

<template>
    <div
        class="shrink-0 border-b border-border/60 bg-violet-50 px-4 py-3 dark:bg-violet-950/30"
    >
        <div
            class="mb-1.5 flex items-center justify-between text-xs font-medium"
        >
            <span
                class="flex items-center gap-1.5 text-violet-700 dark:text-violet-300"
            >
                <BrainCircuit class="h-3.5 w-3.5" />
                {{ featureLabel ?? 'IA en cours…' }}
            </span>
            <span class="text-muted-foreground">{{ countersLabel }}</span>
        </div>

        <div
            class="relative h-1.5 w-full overflow-hidden rounded-full bg-violet-200 dark:bg-violet-800"
        >
            <div
                v-if="progress === 0"
                class="absolute inset-0 -translate-x-full animate-[shimmer_1.4s_ease-in-out_infinite] rounded-full bg-violet-500/60"
            />
            <div
                v-else
                class="h-full rounded-full bg-violet-600 transition-all duration-500 ease-out dark:bg-violet-400"
                :style="{ width: `${progress}%` }"
            />
        </div>

        <p
            v-if="currentPaperTitle"
            class="mt-1 truncate text-[10px] text-muted-foreground"
        >
            Article en cours : {{ currentPaperTitle }}
        </p>
    </div>
</template>
